import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useGetOne } from "react-admin";
import { buildPayload } from "../buildPayload";
import { parseUserIds, trim } from "../text";

const ROLE_LABELS = {
  admin: "admin",
  owner: "owner",
  creator: "creator",
  member: "member",
  guest: "guest",
};

const joinRoles = (roles) => {
  const labels = (roles || []).map((role) => ROLE_LABELS[role] || role);
  if (labels.length === 0) {
    return "";
  }
  if (labels.length === 1) {
    return labels[0];
  }
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
};

// NOTIF-06 / NOTIF-17 AC1a — the resolved-audience sentence, shared by the
// review step and the second (wide-audience) confirmation dialog.
// `workspaceName`/`workspaceLookupFailed` only matter for a resolved single
// workspace, which is never a "wide" audience — ConfirmPushDialog never hits
// that branch, so it calls this with neither.
export const describeAudience = (state, { workspaceName, workspaceLookupFailed } = {}) => {
  if (state.audience_type === "all_users") {
    return "All users on the platform.";
  }

  if (state.audience_type === "workspace") {
    const roles = joinRoles(state.audience_roles);
    const workspaceId = trim(state.workspace_id);

    if (!workspaceId) {
      return `Every ${roles} across all workspaces.`;
    }
    if (workspaceLookupFailed) {
      return `Every ${roles} in workspace ${workspaceId} (could not resolve the workspace name).`;
    }
    return `Every ${roles} in ${workspaceName || workspaceId}.`;
  }

  if (state.audience_type === "user") {
    const count = parseUserIds(state.user_ids).length;
    return `The ${count} pasted user id${count === 1 ? "" : "s"}.`;
  }

  return "";
};

export default function ReviewStep({ state, submitting, disabled = false, onSendClick }) {
  const workspaceId = trim(state.workspace_id);
  const hasWorkspace = state.audience_type === "workspace" && Boolean(workspaceId);

  const { data: workspaceRecord, error: workspaceError } = useGetOne(
    "workspaces",
    { id: workspaceId },
    { enabled: hasWorkspace }
  );

  const audienceText = describeAudience(state, {
    workspaceName: workspaceRecord?.name,
    workspaceLookupFailed: hasWorkspace && Boolean(workspaceError),
  });

  const payload = buildPayload(state);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          Audience
        </Typography>
        <Typography variant="body2">{audienceText}</Typography>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          Message
        </Typography>
        <Typography variant="body2">{state.message}</Typography>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          Link
        </Typography>
        <Typography variant="body2">{state.action_url}</Typography>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          Type / Category
        </Typography>
        <Typography variant="body2">
          {state.type} / {state.category}
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          Push notification
        </Typography>
        <Typography variant="body2">
          {state.send_push_notification
            ? `On — "${state.push_title}"${
                state.push_subtitle ? ` / "${state.push_subtitle}"` : ""
              }`
            : "Off"}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary">
        Delivery is queued once you send. There is no recall or undo.
      </Typography>

      {state.audience_type === "user" && (
        <Typography variant="body2" color="text.secondary">
          User ids are not verified before sending — an id that does not exist resolves to no
          recipient.
        </Typography>
      )}

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          Payload
        </Typography>
        <Box component="pre" data-testid="payload-json" sx={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
          {JSON.stringify(payload, null, 2)}
        </Box>
      </Box>

      <Box>
        <Button variant="contained" onClick={onSendClick} disabled={submitting || disabled}>
          {submitting ? "Sending…" : "Send"}
        </Button>
      </Box>
    </Box>
  );
}
